import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, ModalProps } from '../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Input } from '../CommonComponents/OPL/Input/Input';
import { Textarea } from '../CommonComponents/OPL/Textarea/Textarea';
import { Toggle } from '../CommonComponents/OPL/Toggle/Toggle';
import { Box } from '../CommonComponents/OPL/Box/Box';
import {
  cloneSignaturePageContent,
  validation,
  FORM_ID,
  FormValues,
  SignaturePageContent,
} from './ManageSignaturePageModal.meta';
import { SignatureFieldsList } from './components/SignatureFieldsList/SignatureFieldsList';

export type ManageSignaturePageMode = 'firm' | 'adviser';

export interface ManageSignaturePageModalChange {
  content: SignaturePageContent;
  allowAdvisersOverrideSignaturePage?: boolean;
}

interface ManageSignaturePageModalBaseProps extends Pick<ModalProps, 'isOpen'> {
  content: SignaturePageContent;
  onClose(): void;
  onChange(change: ManageSignaturePageModalChange): void;
}

interface FirmManageSignaturePageModalProps extends ManageSignaturePageModalBaseProps {
  mode: 'firm';
  allowAdvisersOverrideSignaturePage: boolean;
}

interface AdviserManageSignaturePageModalProps extends ManageSignaturePageModalBaseProps {
  mode: 'adviser';
  /** Whether title and body fields are editable. Signature fields always stay enabled. */
  canEditTitleAndBody: boolean;
}

type ManageSignaturePageModalProps =
  | FirmManageSignaturePageModalProps
  | AdviserManageSignaturePageModalProps;

export const ManageSignaturePageModal = (props: ManageSignaturePageModalProps) => {
  const { isOpen, mode, content, onClose, onChange } = props;
  const isFirm = mode === 'firm';
  const canEditTitleAndBodyProp = mode === 'adviser' ? props.canEditTitleAndBody : false;
  const allowAdvisersOverrideSignaturePage =
    mode === 'firm' ? props.allowAdvisersOverrideSignaturePage : false;
  const canEditTitleAndBody = isFirm || canEditTitleAndBodyProp;

  const initialContent = cloneSignaturePageContent(content);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validation(canEditTitleAndBody)),
    defaultValues: {
      title: initialContent.title,
      body: initialContent.body,
      signatureFields: initialContent.signatureFields,
      allowAdvisersOverrideSignaturePage,
    },
  });

  const onSubmit = (values: FormValues) => {
    onChange({
      content: {
        signatureFields: values.signatureFields,
        ...(canEditTitleAndBody && {
          title: values.title,
          body: values.body,
        }),
      },
      ...(isFirm && {
        allowAdvisersOverrideSignaturePage: values.allowAdvisersOverrideSignaturePage,
      }),
    });
    onClose();
  };

  const handleFormSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    handleSubmit(onSubmit)(evt);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      size="large"
      aria={{ labelledby: 'manage-signature-page-modal-header' }}
    >
      <ModalHeader as="h2" id="manage-signature-page-modal-header">
        Manage Signature Page
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <form id={FORM_ID} onSubmit={handleFormSubmit}>
          <Box display="flex" flexDirection="column" gridGap="24">
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  label="Title"
                  {...field}
                  value={field.value}
                  disabled={!canEditTitleAndBody}
                  formVariant={errors.title ? 'error' : undefined}
                  helperText={errors.title?.message}
                  aria-label="Signature page title"
                />
              )}
            />

            <Controller
              name="body"
              control={control}
              render={({ field }) => (
                <Textarea
                  label="Body Text"
                  rows={4}
                  {...field}
                  value={field.value}
                  disabled={!canEditTitleAndBody}
                  formVariant={errors.body ? 'error' : undefined}
                  helperText={errors.body?.message}
                  resize="vertical"
                  aria-label="Signature page body text"
                />
              )}
            />

            <SignatureFieldsList control={control} errorMessage={errors.signatureFields?.message} />

            {isFirm && (
              <Toggle {...register('allowAdvisersOverrideSignaturePage')}>
                Allow Advisors to Override Signature Page
              </Toggle>
            )}
          </Box>
        </form>
      </ModalBody>

      <ModalFooter hasBorder>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button ml="8" type="button" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
